import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  flattenNav,
  navForRole,
  ROLE_LABELS,
  type NavItem,
  type NavSection,
} from '@cannery/shared';
import { useAuth } from '../auth/AuthContext';
import { Icon, type IconName } from './Icon';
import { EnvironmentBadge } from './EnvironmentBadge';

const NAV_ICONS: Record<string, IconName> = {
  dashboard: 'dashboard',
  transactions: 'transactions',
  monitoring: 'monitoring',
  exceptions: 'alert',
  accountability: 'users',
  comparison: 'reports',
  washing: 'washing',
  folding: 'folding',
  purchasing: 'purchasing',
  inventory: 'inventory',
  reports: 'reports',
  settings: 'settings',
  users: 'users',
  pickup: 'pickup',
  delivery: 'delivery',
  expenses: 'expenses',
  reconciliation: 'shield',
  handover: 'delivery',
  'control-review': 'shield',
  backup: 'database',
};

const GROUP_ICONS: Record<string, IconName> = {
  operations: 'transactions',
  'inventory-expense': 'inventory',
  system: 'settings',
};

/** Remembered expand/collapse state for the sidebar groups (per browser). */
const COLLAPSED_KEY = 'clh.navGroups';

function loadCollapsed(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

/**
 * A nav item is "active" when the full location (path + hash) matches its
 * target exactly — three System Settings entries share /admin/settings and
 * must not all highlight at once.
 */
function itemActive(item: NavItem, current: string): boolean {
  return current === item.path;
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent text-accent-ink shadow-[0_0_20px_-6px_rgba(36,152,232,0.5)]">
        <Icon name="washing" className="h-5 w-5" />
      </span>
      {!compact && (
        <span className="leading-tight">
          <span className="block text-sm font-bold text-slate-900">Cannery</span>
          <span className="block text-xs font-medium text-slate-500">Laundry Hub</span>
        </span>
      )}
    </div>
  );
}

/** Admin VIEW filter: ALL BRANCHES / LH1 / LH2. Never a permission restriction. */
function BranchSwitcher() {
  const { branches, branchFilter, setBranchFilter } = useAuth();
  const options = ['ALL', ...branches.map((b) => b.code)];
  return (
    <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1" role="group" aria-label="Branch filter">
      {options.map((code) => {
        const active = branchFilter === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setBranchFilter(code)}
            aria-pressed={active}
            className={`h-9 rounded-lg px-3 text-sm font-semibold transition-colors ${
              active
                ? 'bg-accent text-accent-ink'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200'
            }`}
          >
            {code === 'ALL' ? 'All' : code}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Renders the role's nav sections: standalone items plus expandable groups.
 * Capability filtering already happened upstream (AppShell), so everything
 * here is visible for this role — the backend remains the security boundary.
 */
function NavTree({ sections, idPrefix }: { sections: NavSection[]; idPrefix: string }) {
  const location = useLocation();
  const current = location.pathname + location.hash;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsed);

  function toggle(groupId: string) {
    setCollapsed((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
      } catch {
        // Storage unavailable (private mode) — state still applies for this session.
      }
      return next;
    });
  }

  const linkCls = (active: boolean) =>
    `flex items-center gap-3 rounded-full px-4 py-2 text-sm font-medium transition-all ${
      active
        ? 'bg-accent text-accent-ink font-semibold shadow-[0_0_0_1px_rgba(36,152,232,0.25),0_22px_44px_-26px_rgba(36,152,232,0.35)]'
        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
    }`;

  const renderItem = (item: NavItem) => {
    const active = itemActive(item, current);
    return (
      <Link
        key={item.id}
        to={item.path}
        aria-current={active ? 'page' : undefined}
        className={linkCls(active)}
      >
        <Icon name={NAV_ICONS[item.id] ?? 'dashboard'} className="h-5 w-5 shrink-0" />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <nav className="flex flex-col gap-1" aria-label="Main navigation">
      {sections.map((section) => {
        if (section.kind === 'item') return renderItem(section.item);

        const { group } = section;
        const open = !collapsed[group.id];
        const headerCls =
          'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-semibold tracking-wide uppercase transition-colors text-slate-600 hover:bg-slate-100 hover:text-slate-900';

        return (
          <div key={group.id} className="mt-3 first:mt-0">
            <button
              type="button"
              onClick={() => toggle(group.id)}
              aria-expanded={open}
              aria-controls={`${idPrefix}-nav-${group.id}`}
              className={headerCls}
            >
              <Icon name={GROUP_ICONS[group.id] ?? 'dashboard'} className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">{group.label}</span>
              <Icon
                name="chevronDown"
                className={`h-4 w-4 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`}
              />
            </button>
            {open && (
              <div
                id={`${idPrefix}-nav-${group.id}`}
                className={`ml-3 mt-1 space-y-1 border-l pl-2 border-slate-200`}
              >
                {group.items.map(renderItem)}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

/** The width the desktop shell is laid out at (sidebar 256 + content 1024). */
const DESIGN_WIDTH = 1280;

/**
 * Proportional-downscale floor. The design scales to fit the viewport down
 * to this ratio; below it (very narrow phones) the canvas keeps its
 * proportions and the page pans horizontally instead — at smaller ratios
 * the 14px base text and 36px icon buttons become unusable. Structure and
 * proportions are preserved either way (requirement: fit while readable,
 * then provide scrolling).
 */
const MIN_SCALE = 0.55;

function computeScale(): number {
  if (typeof document === 'undefined') return 1;
  // clientWidth (not innerWidth) excludes the vertical scrollbar, so the
  // scaled canvas always fits the genuinely usable width.
  const usable = document.documentElement.clientWidth || window.innerWidth;
  return Math.min(1, Math.max(MIN_SCALE, usable / DESIGN_WIDTH));
}

/**
 * PC-first scaling (single design everywhere): the shell always lays out at
 * DESIGN_WIDTH and, when the viewport is narrower, the whole canvas scales
 * down proportionally with standardized CSS `zoom` (Chromium/Safari/Firefox
 * 126+). No mobile/tablet variants — one desktop layout at every size.
 */
function useDesktopScale(): number {
  const [scale, setScale] = useState(computeScale);
  useEffect(() => {
    const update = () => setScale(computeScale());
    window.addEventListener('resize', update);
    // The usable width also changes when the page's scrollbar appears after
    // the first content render (no window resize fires then), so observe the
    // root element too. Stable once rendered: vertical overflow persists at
    // every scale step, so this cannot oscillate.
    const ro = new ResizeObserver(update);
    ro.observe(document.documentElement);
    return () => {
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, []);
  return scale;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, capabilities, logout } = useAuth();
  const location = useLocation();
  const scale = useDesktopScale();
  const scaled = scale < 1;

  // Navigation scroll behaviour: section anchors (e.g. settings#employees)
  // scroll into view; every other navigation starts at the top of the page.
  // The settings sections load their data asynchronously and change height
  // above the anchor, so the scroll is re-applied briefly while the page
  // settles instead of landing at a stale offset.
  useEffect(() => {
    const scrollToAnchor = () => {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView({ block: 'start' });
      return Boolean(el);
    };

    if (!location.hash || !scrollToAnchor()) {
      window.scrollTo(0, 0);
      return;
    }
    const t1 = setTimeout(scrollToAnchor, 150);
    const t2 = setTimeout(scrollToAnchor, 450);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.pathname, location.hash]);

  if (!user) return null;

  const isAdmin = user.role === 'ADMIN';
  // Hide anything the role lacks the capability for; groups with no visible
  // children disappear entirely. This shapes presentation only.
  const sections: NavSection[] = navForRole(user.role).flatMap(
    (section): NavSection[] => {
      if (section.kind === 'item') {
        return capabilities.includes(section.item.capability) ? [section] : [];
      }
      const items = section.group.items.filter((i) => capabilities.includes(i.capability));
      return items.length > 0
        ? [{ kind: 'group', group: { ...section.group, items } }]
        : [];
    },
  );
  const nav = flattenNav(sections);
  const current = location.pathname + location.hash;
  const active = nav.find((n) => itemActive(n, current)) ?? nav[0];
  const initials = user.name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

  // Inside a `zoom`ed subtree viewport units still render scaled, so a true
  // full-viewport height must be expressed in unscaled px via calc().
  const viewportHeight = scaled ? 'calc(100dvh / var(--clh-scale))' : '100dvh';

  return (
    <div
      className="flex bg-slate-50"
      style={
        {
          minHeight: viewportHeight,
          ...(scaled ? { zoom: scale, width: DESIGN_WIDTH, '--clh-scale': scale } : {}),
        } as CSSProperties
      }
    >
      {/* Sidebar — PC-first: always visible, same 256px in design px at every
          viewport (the canvas scales as one). Sticky so it stays pinned while
          the page scrolls; the nav list scrolls on wheel/keyboard with its
          visible scrollbar hidden via .no-scrollbar (index.css). */}
      <aside
        className="sticky top-0 z-40 flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white/95 px-4 py-5 backdrop-blur-md shadow-[10px_0_40px_-30px_rgba(0,0,0,0.08)]"
        style={{ height: viewportHeight }}
      >
        <Brand />
        <div className="no-scrollbar mt-8 flex-1 overflow-y-auto">
          <NavTree sections={sections} idPrefix="side" />
        </div>
        <div className="border-t border-slate-200 pt-4">
          <p className="truncate text-sm font-semibold text-slate-900">{user.name}</p>
          <p className="text-xs text-slate-500">
            {ROLE_LABELS[user.role]}
            {user.branchCode ? ` · ${user.branchCode}` : ''}
          </p>
        </div>
      </aside>

      {/* Right region — the same desktop header bar and content column at every
          width; the header stays fully visible because the canvas, not the
          viewport, determines its width. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md">
          <div className="flex min-w-0 items-center gap-2">
            <h1 className="truncate text-lg font-semibold text-slate-900">{active?.label}</h1>
            <EnvironmentBadge />
          </div>

          <div className="ml-auto flex items-center gap-3">
            {isAdmin && <BranchSwitcher />}
            {!isAdmin && user.branchCode && (
              <span className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600">
                {user.branchCode}
              </span>
            )}
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700">
                {initials}
              </span>
              <span className="leading-tight">
                <span className="block max-w-40 truncate text-sm font-semibold text-slate-800">
                  {user.name}
                </span>
                <span className="block text-xs text-slate-500">{ROLE_LABELS[user.role]}</span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              aria-label="Log out"
              title="Log out"
              className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              <Icon name="logout" className="h-5 w-5" />
            </button>
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl px-8 py-6">{children}</main>
      </div>
    </div>
  );
}
